import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { apiMessage, receivingApi, type HistoryEvent } from '../../lib/receiving';

/**
 * An order's whole story (spec §8): every scan, close, call, decision, customer message and
 * anomaly event, dated day and time, with the author, in order across days.
 */
export function OrderHistoryDialog({
  orderId,
  orderNumber,
  onClose,
}: {
  orderId: string | null;
  orderNumber: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<HistoryEvent[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!orderId) return;
    setRows(null);
    setError('');
    receivingApi
      .history(orderId)
      .then((r) => setRows(r.data))
      .catch((err) => setError(apiMessage(err)));
  }, [orderId]);
  return (
    <Modal
      open={!!orderId}
      wide
      title={t('rcv.history.title', { number: orderNumber ?? '' })}
      onClose={onClose}
    >
      {error && <p className="text-destructive">{error}</p>}
      {!rows && !error && <p className="text-muted-foreground">{t('rcv.loading')}</p>}
      {rows && rows.length === 0 && (
        <p className="text-muted-foreground">{t('rcv.history.empty')}</p>
      )}
      {rows && rows.length > 0 && (
        <ol className="space-y-3">
          {rows.map((e, i) => (
            <li key={i} className="border-l-4 border-muted pl-3">
              <div className="text-base">
                <strong>
                  {new Date(e.at).toLocaleString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </strong>
                {' · '}
                {e.who ?? t('rcv.system')}
                <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs uppercase">
                  {t(`rcv.history.kind.${e.kind}`, { defaultValue: e.kind })}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">{e.what}</div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
